import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Button, Checkbox, Grid, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { IvrUser } from "../../../../Models/User";
import ToolWrapper from "../ToolWrapper";
import IvrUserSkillsConfigCard from "./IvrUserSkillsConfig.Card";
import useGetIvrCampaigns from "./IvrUserSkillsConfig.UseGetCampaigns";
import useGetIvrUsers from "./IvrUserSkillsConfig.UseGetIvrUsers";
import usePutIvrUserCampaign from "./IvrUserSkillsConfig.UsePutIvrUserCampaign";
import IvrUserSkillsConfigUsersList from "./IvrUserSkillsConfig.UsersList";

export interface UserCampaignUpdates {
  userIds: string[];
  campaignIds: number[];
}
export interface UserTeam {
  team: string;
  users: { user: IvrUser; selected: boolean }[];
}
export interface IvrCampaign {
  id: number;
  name: string;
}
const toolReadme = [""];

const IvrUserSkillsConfig: React.FC = () => {
  const [teams, setTeams] = useState<UserTeam[]>();
  const [campaigns, setCampaigns] = useState<{ campaign: IvrCampaign; selected: boolean }[]>([]);
  const [updateResults, setUpdateResults] = useState<{
    results: { name: string; isSuccess: boolean; message: string }[];
    successes: number;
    failures: number;
  }>();
  const [disabled, setDisabled] = useState(false);

  const usersRef = useRef<IvrUser[]>([]);

  const { res: usersResponse } = useGetIvrUsers();
  useEffect(() => {
    if (!usersResponse) return;
    setTeams(
      usersResponse.reduce((teamGroups, user) => {
        let teamIndex = teamGroups.findIndex(({ team }) => team === user.ivrTeamName);
        if (teamIndex === -1) teamIndex = teamGroups.push({ team: user.ivrTeamName, users: [] }) - 1;
        teamGroups[teamIndex].users.push({ user, selected: false });
        return teamGroups;
      }, [] as UserTeam[])
    );
    usersRef.current = usersResponse;
  }, [usersResponse]);

  const { res: campaignsResponse, loading: campaignsLoading } = useGetIvrCampaigns();
  useEffect(() => {
    if (campaignsResponse) setCampaigns(campaignsResponse.map(campaign => ({ campaign, selected: false })));
  }, [campaignsResponse]);

  const { res: updateResponses, loading: updatesLoading, setRequest } = usePutIvrUserCampaign();
  useEffect(() => {
    if (updateResponses) {
      const successes = updateResponses.filter(ur => ur.isSuccess).length;
      setUpdateResults({
        successes,
        failures: updateResponses.length - successes,
        results: updateResponses.map(({ id, isSuccess, message }) => {
          const user = usersRef.current.find(user => user.id === id);
          return {
            name: (user && `${user.firstName} ${user.lastName}, ${user.ivrTeamName}`) || "",
            isSuccess,
            message
          };
        })
      });
    }
  }, [updateResponses]);

  const toggleCampaign = (index: number) => () =>
    setCampaigns(campaigns => {
      campaigns[index].selected = !campaigns[index].selected;
      return [...campaigns];
    });
  const assignCampaigns = () => {
    if (!teams) return;
    setDisabled(true);
    setRequest({
      userIds: teams.reduce(
        (userIds, team) => userIds.concat(team.users.filter(({ selected }) => selected).map(({ user: { id } }) => id)),
        [] as string[]
      ),
      campaignIds: campaigns.filter(({ selected }) => selected).map(({ campaign: { id } }) => id)
    });
  };
  const reset = () => {
    if (!teams) return;
    setCampaigns(campaigns => campaigns.map(({ campaign }) => ({ campaign, selected: false })));
    setTeams(teams =>
      teams?.map(({ team, users }) => ({
        team,
        users: users.map(({ user }) => ({ user, selected: false }))
      }))
    );
    setDisabled(false);
    setUpdateResults(undefined);
  };
  const campaignsSelectedCount = campaigns.filter(u => u.selected).length;

  return (
    <ToolWrapper title="IVR User Skills Settings" readme={toolReadme} isLoading={false}>
      <Grid
        container
        spacing={4}
        wrap="nowrap"
        alignItems="center"
        sx={{ width: "100%", height: "100%", overflow: "hidden", m: 0, p: 2 }}
      >
        <Grid item xs sx={{ height: "100%" }}>
          <IvrUserSkillsConfigUsersList
            isDisabled={disabled}
            ivrUserTeams={teams}
            onChange={value => setTeams(value)}
          />
        </Grid>
        <Grid item sx={{ height: "100%" }}>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={1}
            sx={{ height: "100%" }}
          >
            <Grid item>
              <Button onClick={reset} disabled={updatesLoading} color="secondary" size="small">
                Reset
              </Button>
            </Grid>
            <Grid item>
              <Button
                size="small"
                onClick={assignCampaigns}
                disabled={
                  disabled ||
                  updatesLoading ||
                  !teams?.some(({ users }) => users.some(({ selected }) => selected)) ||
                  !campaigns.some(c => c.selected)
                }
              >
                Confirm
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs sx={{ height: "100%" }}>
          <IvrUserSkillsConfigCard
            title="Campaigns"
            subheader={campaignsSelectedCount ? `${campaignsSelectedCount} Selected` : undefined}
            isLoading={campaignsLoading}
          >
            <List>
              {campaigns.map(({ campaign: { name }, selected }, index) => (
                <ListItem key={index} button onClick={toggleCampaign(index)} disabled={disabled}>
                  <ListItemIcon>
                    <Checkbox checked={selected} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText primary={name} />
                </ListItem>
              ))}
            </List>
          </IvrUserSkillsConfigCard>
        </Grid>
        <Grid item xs={5} sx={{ height: "100%" }}>
          <IvrUserSkillsConfigCard title="Assignments" isLoading={updatesLoading}>
            {updateResults && (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                  {!!updateResults.successes && (
                    <>
                      <Typography color="success" variant="body2" component="span">{`${
                        updateResults.successes
                      } Success${updateResults.successes === 1 ? "" : "es"}`}</Typography>
                      <CheckCircleIcon color="success" />
                    </>
                  )}
                  {!!updateResults.failures && (
                    <>
                      <Typography variant="body2" component="span" color="error">{`${updateResults.failures} Failure${
                        updateResults.failures === 1 ? "" : "s"
                      }`}</Typography>
                      <ErrorIcon color="error" />
                    </>
                  )}
                </Stack>
                <List>
                  {!!updateResults &&
                    updateResults.results.map(({ name, isSuccess, message }, index) => (
                      <ListItem
                        sx={theme => (!isSuccess ? { backgroundColor: theme.palette.error.light } : {})}
                        divider
                        key={index}
                      >
                        <ListItemText primary={name} secondary={message} />
                      </ListItem>
                    ))}
                </List>
              </Stack>
            )}
          </IvrUserSkillsConfigCard>
        </Grid>
      </Grid>
    </ToolWrapper>
  );
};

export default IvrUserSkillsConfig;
